import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DelayService, TG_INPUT, WindowRef } from '@trademe/tangram';
import { fromEvent, Observable, race, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import {
    IHostAckMessage,
    IHostCostUpdateMessage,
    IHostInitializationConfigurationMessage,
    IHostRequestPayloadMessage,
    IHostToWidgetMessage
} from './host-to-widget-message-types';

import {
    isIWidgetToHostMessage,
    IWidgetCreditCardRedirectMessage,
    IWidgetErrorMessage,
    IWidgetPayload,
    IWidgetPayloadMessage,
    IWidgetStyleMessage,
    IWidgetToHostMessage
} from './widget-to-host-message-types';
import { CreditAccountEnvironmentService } from '../environment/credit-account-environment.service';
import { ExtractCreditCardRedirectResponseData, ICreditCardRedirectData, ICreditCardRedirectResponseData } from './credit-card-redirect-helper';
import { UserFacade } from '@classifieds-sell/state';

const MAX_IFRAME_TIMEOUT_TIME = 20000;

@Component({
    selector: 'tm-ping-widget',
    templateUrl: './ping-widget.component.html',
    styleUrls: ['./ping-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: TG_INPUT,
            // eslint-disable-next-line
            useExisting: forwardRef(() => PingWidgetComponent)
        },
        {
            provide: NG_VALUE_ACCESSOR,
            // eslint-disable-next-line
            useExisting: forwardRef(() => PingWidgetComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            // eslint-disable-next-line
            useExisting: forwardRef(() => PingWidgetComponent),
            multi: true
        }
    ],
    standalone: false
})
export class PingWidgetComponent
    implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    @Input() public allowPaymentWithNegativeBalance = false;
    @Input() public totalToPay = 0;
    @Input() public triggerCardSubmit$: Observable<number>;
    @Input() public hidePingLogo = false;

    @Output() public loadedCallback = new EventEmitter<boolean>();
    @Output() public successCallback = new EventEmitter<IWidgetPayload>();
    @Output() public errorCallback = new EventEmitter<IWidgetErrorMessage>();

    @ViewChild('pingWidget') public iFrameElement:
        | ElementRef<HTMLIFrameElement>
        | undefined;

    private _destroyed$ = new Subject<void>();

    private _iframeMessageEvents$: Observable<IWidgetToHostMessage> | undefined;

    public paymentsIframeUrl: string;
    public iFrameSrc: SafeResourceUrl;
    private _redirectResponseData: ICreditCardRedirectResponseData | undefined;

    private _memberSummary = this._userFacade.memberSummary$;

    constructor(
        private _sanitizer: DomSanitizer,
        private _delayService: DelayService,
        private _windowRef: WindowRef,
        private _creditAccountEnvironmentService: CreditAccountEnvironmentService,
        private _userFacade: UserFacade
    ) {}

    public ngOnInit() {
        const env = this._creditAccountEnvironmentService.getCreditAccountEnvironmentDetails();

        this.paymentsIframeUrl = env.paymentsUrl;
        const apiBaseUrl = env.apiBaseUrlForGringotts;

        if (env.isProduction) {
            this.iFrameSrc =
                this._sanitizer.bypassSecurityTrustResourceUrl(
                    this.paymentsIframeUrl +
                        (this.paymentsIframeUrl.endsWith('/')
                            ? ''
                            : '/') +
                        'widget.html'
                );
        } else {
            this.iFrameSrc =
                this._sanitizer.bypassSecurityTrustResourceUrl(
                    this.paymentsIframeUrl +
                        (this.paymentsIframeUrl.endsWith('/')
                            ? ''
                            : '/') +
                        'widget.html?backendoverride=' +
                        apiBaseUrl
                );
        }

        this._windowRef.useWindow((tmWindow) => {
            this._redirectResponseData = ExtractCreditCardRedirectResponseData(this._windowRef);

            this._iframeMessageEvents$ = fromEvent<MessageEvent>(
                tmWindow,
                'message'
            ).pipe(
                filter(
                    (event) =>
                        this.paymentsIframeUrl.startsWith(event.origin) &&
                        isIWidgetToHostMessage(event.data)
                ),
                map((event) => event.data)
            );

            this._iframeMessageEvents$.pipe(takeUntil(this._destroyed$)).subscribe(
                (message) => this.onMessageEventReceived(message)
            );
        });
    }

    public ngOnDestroy() {
        this._destroyed$.next();
    }

    public ngAfterViewInit(): void {
        fromEvent(this.iFrameElement.nativeElement, 'load')
            .pipe(takeUntil(this._destroyed$))
            .subscribe(() => this.onIFrameLoaded());
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { totalToPay } = changes;

        if (totalToPay && totalToPay.currentValue) {
            this.updateTotalToPay(totalToPay.currentValue);
        }
    }

    private onIFrameLoaded() {
        // This checks to see that the iframe has loaded within a certain amount of time. If not an error is logged.
        race(
            this._iframeMessageEvents$,
            this._delayService.createDelay(MAX_IFRAME_TIMEOUT_TIME)
        )
            .pipe(take(1))
            .subscribe((event) => {
                let isIFrameMessage = false;

                if ((event as IWidgetToHostMessage).type) {
                    isIFrameMessage = true;
                }

                if (isIFrameMessage) {
                    this.loadedCallback.emit();
                }
            });

        let includeCreditCardId: number | undefined;
        let selectBalance: boolean | undefined;

        if (this._redirectResponseData) {
            includeCreditCardId = this._redirectResponseData.creditCardId;
            if (this._redirectResponseData.widgetState) {
                selectBalance = this._redirectResponseData.widgetState.balanceSelected;
            }
        }

        this.postMessage(<IHostInitializationConfigurationMessage>{
            type: 'host-initialization-configuration',
            data: {
                initialTotal: this.totalToPay,
                autobillerFormatting: false,
                allowedFundSourceTypes: ['all'],
                allowPaymentWithNegativeBalance: this.allowPaymentWithNegativeBalance,
                showAddCardOnLoadWhenNoValidFundSource: false,
                addCardsOnSeparatePage: true,
                includeCreditCardId: includeCreditCardId,
                selectBalance: selectBalance,
                hidePingLogo: this.hidePingLogo
            }
        });
    }

    private postMessage(message: IHostToWidgetMessage) {
        const nativeIFrameElementContentWindow = this.iFrameElement
            .nativeElement.contentWindow as WindowProxy;

        nativeIFrameElementContentWindow.postMessage(
            message,
            this.paymentsIframeUrl
        );
    }

    private base64UrlEncode(input: string): string {
        // Encode the input string using btoa
        const base64 = btoa(input);

        // Convert the Base64 to a URL-safe version
        const base64Url = base64
            .replace(/\+/g, '-') // Replace + with -
            .replace(/\//g, '_') // Replace / with _
            .replace(/=+$/, ''); // Remove ending =

        return base64Url;
    }

    private apiUrl(): string {
        const apiBase = this._creditAccountEnvironmentService
            .getCreditAccountEnvironmentDetails().apiBaseUrlForGringotts;
        return encodeURI(apiBase);
    }

    private onMessageEventReceived(message: IWidgetToHostMessage) {
        switch (message.type) {
            case 'widget-initialization':
                // flag that we can now send messages to the widget and update the charged amount
                this.postMessage(<IHostAckMessage>{
                    type: 'host-ack'
                });
                break;
            case 'widget-focus':
                // ensure that ping is the selected payment method
                break;
            case 'widget-payload':
                {
                    // return payment information object to the parent
                    const payloadMessage = <IWidgetPayloadMessage> message;

                    this.successCallback.emit(payloadMessage.data);
                    break;
                }
            case 'widget-style':
                // resize the iframe element to the size required
                {
                    const styleMessage = <IWidgetStyleMessage>message;

                    if (
                        styleMessage.data.height &&
                        this.iFrameElement.nativeElement.scrollHeight !==
                            styleMessage.data.height
                    ) {
                        this.iFrameElement.nativeElement.style.height = `${styleMessage.data.height}px`;
                    }
                }
                break;
            case 'widget-creditcard-redirect':
                {
                    const redirectMessage = <IWidgetCreditCardRedirectMessage>message;

                    const balanceSelected = redirectMessage.data.balanceSelected;

                    this._memberSummary.subscribe(member => {
                            let photoUrl = member.memberProfile?.photo;
                            if (
                                photoUrl &&
                                [
                                    '/images/kiwi130.gif',
                                    '/images/kiwi130-v2.gif',
                                    '/images/kev_no_profile_image.png'
                                ].some(i => photoUrl.includes(i))
                            ) {
                                photoUrl = undefined;
                            }
                            const lastNumberOfMemberId = member.memberId % 10;
                            const firstLetterOfMemberNickname = member.nickname?.toUpperCase()[0];

                            this._windowRef.useWindow(window => {
                                const hideSaveForLater = false;
                                const addCardPreviousStepTitle = 'Top up amount';
                                const addCardNextStepTitle = 'Finalise Top up';

                                const queryStringParams = new URLSearchParams(window.location.search);
                                const queryStringArray: [string, string][] = [];

                                queryStringParams.forEach((val, key) => {
                                    queryStringArray.push([key, val]);
                                });

                                const paymentSessionRequest = <ICreditCardRedirectData>{
                                    returnTo: window.location.origin.toString() + window.location.pathname.toString(),
                                    customisation: {
                                        hideSaveForLater: hideSaveForLater,
                                        previousStepTitle: addCardPreviousStepTitle,
                                        nextStepTitle: addCardNextStepTitle,
                                        userAvatarLetter: firstLetterOfMemberNickname,
                                        userAvatarStyleNumber: lastNumberOfMemberId,
                                        userIconUrl: photoUrl
                                    },
                                    widgetState: {
                                        balanceSelected: balanceSelected
                                    },
                                    queryStringParams: queryStringArray
                                };

                                const encodedPaymentSessionRequest = this.base64UrlEncode(
                                    JSON.stringify(paymentSessionRequest)
                                );

                                const isProd = this._creditAccountEnvironmentService
                                    .getCreditAccountEnvironmentDetails().isProduction;

                                const urlBase = this.paymentsIframeUrl;
                                let backendOverride = '';
                                if (!isProd) {
                                    backendOverride = this.apiUrl();
                                }

                                const addCreditCardRedirectPage = new URL('secure-card-entry.html', urlBase);
                                addCreditCardRedirectPage.searchParams.append(
                                    'psr',
                                    encodedPaymentSessionRequest
                                );

                                if (backendOverride) {
                                    addCreditCardRedirectPage.searchParams.append(
                                        'backendoverride',
                                        backendOverride
                                    );
                                }

                                window.location.assign(addCreditCardRedirectPage);
                            });
                        });
                }
                break;
            case 'widget-error':
                {
                    const errorMessage = <IWidgetErrorMessage>message;

                    this.errorCallback.emit(errorMessage);
                }
                break;
        }
    }

    private updateTotalToPay(totalToPay: number) {
        if (this.iFrameElement) {
            this.postMessage(<IHostCostUpdateMessage>{
                type: 'host-cost-update',
                data: {
                    total: totalToPay
                }
            });
        }
    }
}
